import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { Link } from 'react-router-dom'

import { useMayOpen } from '@/features/auth/access'
import { formatMoney, formatPct, getInventory } from '@/features/analytics/api'
import { CoverList, Kpi, PanelCard, WindowPicker } from '@/features/analytics/panels'

/**
 * Turnover, days of cover and how often the shelf is empty.
 *
 * Turnover is annualised cost of sales over the value of the stock holding it, so the two
 * numbers behind it are shown as well: a ratio on its own cannot be checked, and this one
 * is read against the Inventory account often enough that the reader needs both halves.
 *
 * Days of cover is null, not zero, for a product nothing sold. Zero would sort it to the
 * top of the reorder list, which is exactly backwards -- a product with no demand and stock
 * on the shelf has infinite cover, and belongs in the overstocked list underneath.
 */
export function InventoryAnalytics() {
  const [days, setDays] = useState(30)

  const inventory = useQuery({
    queryKey: ['analytics', 'inventory', days, 25],
    queryFn: () => getInventory({ days, limit: 25 }),
    retry: false,
  })

  const health = inventory.data
  const mayOpenValuation = useMayOpen('/stock/valuation')

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">Inventory analytics</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            How fast the shelf turns, how long it lasts, and how often it is empty.
            {mayOpenValuation && (
              <>
                {' '}
                The holding value comes from the{' '}
                <Link to="/stock/valuation" className="underline">
                  stock valuation
                </Link>
                .
              </>
            )}
          </p>
        </div>
        <WindowPicker days={days} onChange={setDays} asOf={health?.as_of} />
      </header>

      <div className="grid gap-4 tablet:grid-cols-2 desktop:grid-cols-4">
        <Kpi
          label="Turnover"
          value={health?.turnover != null ? `${health.turnover.toFixed(2)}×` : '—'}
          hint="annualised, cost over holding"
          loading={inventory.isLoading}
        />
        <Kpi
          label="Cost of sales"
          value={formatMoney(health?.cogs ?? null)}
          hint={`over ${days} days`}
          loading={inventory.isLoading}
        />
        <Kpi
          label="Stock value"
          value={formatMoney(health?.inventory_value ?? null)}
          hint="at weighted average cost"
          loading={inventory.isLoading}
        />
        <Kpi
          label="Stockout rate"
          value={formatPct(health?.stockout_pct)}
          hint={
            health
              ? `${health.stockout_pairs} of ${health.tracked_pairs} product/location lines empty`
              : 'Shelf lines at zero'
          }
          loading={inventory.isLoading}
        />
      </div>

      <div className="grid gap-4 desktop:grid-cols-2">
        <PanelCard
          title="Running out next"
          description="On-hand units over daily demand in this window."
          isLoading={inventory.isLoading}
          error={inventory.error}
          isEmpty={!health?.at_risk.length}
          emptyMessage="Nothing you stock is close to running out."
        >
          <CoverList rows={health?.at_risk ?? []} tone="risk" />
        </PanelCard>

        <PanelCard
          title="Sitting too long"
          description="Stock on the shelf with more cover than it needs."
          isLoading={inventory.isLoading}
          error={inventory.error}
          isEmpty={!health?.overstocked.length}
          emptyMessage="Nothing is overstocked against its demand."
        >
          <CoverList rows={health?.overstocked ?? []} tone="idle" />
        </PanelCard>
      </div>
    </div>
  )
}
